import type { ReactNode } from 'react';

import type { RunIssueViewModel } from '../pages/app/appRunData';

type IssueSeverity = 'critical' | 'high' | 'medium' | 'low' | 'informational' | 'gas' | 'other';

export interface RunIssueViewModelEnhanced extends RunIssueViewModel {
  severity: IssueSeverity;
  cleanTitle: string;
}

const SEVERITY_ORDER: IssueSeverity[] = [
  'critical', 'high', 'medium', 'low', 'informational', 'gas', 'other'
];

export const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Info',
  gas: 'Gas',
  other: 'Other'
};

const runIssueParseSeverity = (title: string, impact: string): IssueSeverity => {
  const match = title.match(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]/i);

  if (match) {
    const severity = match[1].toLowerCase();

    return (severity === 'info' ? 'informational' : severity) as IssueSeverity;
  }

  const normalizedImpact = impact.toLowerCase();

  if (normalizedImpact.includes('critical')) return 'critical';
  if (normalizedImpact.includes('high')) return 'high';
  if (normalizedImpact.includes('medium')) return 'medium';
  if (normalizedImpact.includes('low')) return 'low';
  if (normalizedImpact.includes('info')) return 'informational';
  if (normalizedImpact.includes('gas')) return 'gas';
  return 'other';
};

const runIssueMapBackendSeverity = (severity: string | undefined): IssueSeverity | null => {
  if (!severity) {
    return null;
  }

  switch (severity.toLowerCase()) {
    case 'critical': return 'critical';
    case 'high': return 'high';
    case 'medium': return 'medium';
    case 'low': return 'low';
    case 'info':
    case 'informational': return 'informational';
    case 'gas': return 'gas';
    default: return null;
  }
};

const runIssueCleanTitle = (title: string): string => title
  .replace(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]\s*/i, '')
  .replace(/^(Critical|High|Medium|Low|Informational|Info|Gas)\s*[-\u2013\u2014]\s*/i, '')
  .trim();

export const runIssueEnhanceIssues = (issues: RunIssueViewModel[]): RunIssueViewModelEnhanced[] => issues
  .map((issue) => ({
    ...issue,
    severity: runIssueMapBackendSeverity(issue.severity) ?? runIssueParseSeverity(issue.title, issue.impact),
    cleanTitle: runIssueCleanTitle(issue.title)
  }))
  .sort((left, right) => SEVERITY_ORDER.indexOf(left.severity) - SEVERITY_ORDER.indexOf(right.severity));

function RunMdText({ text }: { text: string }) {
  const regex = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  regex.lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      parts.push(<code key={match.index} className="md-code">{match[1]}</code>);
    } else if (match[2] !== undefined) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={match.index}>{match[3]}</em>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return <>{parts}</>;
}

export function RunMdBlock({ text }: { text: string }) {
  const paragraphs = text.split(/\n\n+/).map((paragraph) => paragraph.trim()).filter(Boolean);

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>
          <RunMdText text={paragraph.replace(/\n/g, ' ')} />
        </p>
      ))}
    </>
  );
}
